import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import loadingStatusCodes from "../../../lib/const/loadingStatusCodes";
import Message from "../../../components/Message/Message";

import {
  getCreateRousingPlaneRoute,
  getRousingPlaneRoute,
  getRousingPlaneSearchRoute,
  getRousingPlaneVersionsRoute,
  getSupplierOfferingssRoute,
  getUpdateRousingPlaneRoute,
} from "../../../lib/routes/routingPlanes";

import { LoadingStatusCode as ILoadingStatusCode } from "../../../lib/types/LoadingStatusCode";
import { AnyObject } from "../../../lib/interfaces/AnyObject";
import {
  ICreateRoutingPlan,
  IRoutingPlan,
  IRoutingPlanVersion,
  IRoutingPlanVersionsList,
  IUpdateRoutingPlan,
} from "./types/routingPlanes";
import { toDisplayFormat } from "../../../lib/utils/formatDate";
import { mapRoutingPlanList, mapRoutingPlanVersions } from "./mappers/fromDto";
import { catchApiError } from "../../../lib/utils/catchApiError";

interface IRoutingPlanesState {
  routingPlanesList: IRoutingPlan[];
  routingPlanesListLoading: ILoadingStatusCode;
  routingOrdersTotalElements: number;
  routingPlanesVersions: IRoutingPlanVersionsList[];
  routingPlanesVersionsLoading: ILoadingStatusCode;
}

const initialState: IRoutingPlanesState = {
  routingPlanesList: [],
  routingPlanesListLoading: loadingStatusCodes.idle,
  routingOrdersTotalElements: 0,
  routingPlanesVersions: [],
  routingPlanesVersionsLoading: loadingStatusCodes.idle,
};

const getConcatValues = (path, name) => {
  let result = "";
  if (path) {
    result = path;
  }
  if (name) {
    if (result) {
      result = `${result} - `;
    }
    result = `${result}${name}`;
  }
  return result;
};

const fetchRoutingPlanesSearch = createAsyncThunk(
  "routing-planes/fetchRoutingPlanesSearch",
  async (searchParams: AnyObject) => {
    try {
      const { data } = await axios(getRousingPlaneSearchRoute(searchParams));
      return data;
    } catch (e) {
      Message("Ошибка загрузки списка планов маршрутизации. Попробуйте позже", e);
    }
  }
);

const fetchRoutingPlane = createAsyncThunk(
  "routing-planes/fetchRoutingPlane",
  async ({ tcOfferingItemId, currencyId }: { tcOfferingItemId: number; currencyId?: number }) => {
    try {
      const searchParams: any = {};
      if (currencyId) {
        searchParams.currencyId = currencyId;
      }
      const { data } = await axios(getRousingPlaneRoute(tcOfferingItemId, searchParams));
      return data;
    } catch (e) {
      Message("Ошибка загрузки плана маршрутизации. Попробуйте позже", e);
    }
  }
);

const fetchCreatePlane = createAsyncThunk(
  "routing-planes/fetchCreatePlane",
  async (body: ICreateRoutingPlan) => {
    try {
      const { data } = await axios.post(getCreateRousingPlaneRoute(), body);
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка при создания",
      });
    }
  }
);

const fetchUpdatePlane = createAsyncThunk(
  "routing-planes/fetchUpdatePlane",
  async (body: IUpdateRoutingPlan) => {
    try {
      const { data } = await axios.patch(getUpdateRousingPlaneRoute(), body);
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка при отправке",
      });
    }
  }
);

const fetchRoutingPlanesVersions = createAsyncThunk(
  "routing-orders/fetchRoutingPlanesVersions",
  async ({ tcOfferingItemId }: { tcOfferingItemId: number }) => {
    try {
      const { data } = await axios(getRousingPlaneVersionsRoute(tcOfferingItemId));
      return data;
    } catch (e) {
      Message("Ошибка загрузки версий маршрута. Попробуйте позже", e);
    }
  }
);

const fetchSupplierOfferings = createAsyncThunk(
  "routing-orders/fetchSupplierOfferings",
  async ({ destinationId, currencyId }: { destinationId: number; currencyId }) => {
    try {
      const { data } = await axios(getSupplierOfferingssRoute({ destinationId, currencyId }));
      return data;
    } catch (e) {
      Message("Ошибка загрузки операторских тарифов. Попробуйте позже", e);
    }
  }
);

const slice = createSlice({
  name: "routingPlans",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoutingPlanesSearch.pending, (state) => {
        state.routingPlanesListLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchRoutingPlanesSearch.fulfilled, (state, { payload }) => {
        state.routingPlanesListLoading = loadingStatusCodes.fulfilled;
        state.routingOrdersTotalElements = payload?.totalElements;
        state.routingPlanesList = mapRoutingPlanList(payload);
      })
      .addCase(fetchRoutingPlanesVersions.fulfilled, (state, action) => {
        state.routingPlanesVersions = mapRoutingPlanVersions(action);
        state.routingPlanesVersionsLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchRoutingPlanesVersions.pending, (state) => {
        state.routingPlanesVersionsLoading = loadingStatusCodes.pending;
      });
  },
});

const { reducer } = slice;

export {
  reducer,
  fetchRoutingPlanesSearch,
  fetchRoutingPlanesVersions,
  fetchRoutingPlane,
  fetchSupplierOfferings,
  fetchCreatePlane,
  fetchUpdatePlane,
};
